/**
 * Shared display helpers — core subset extracted for unified page slices.
 *
 * Only functions whose complete closure is satisfied by core deps are included here.
 * Add entries incrementally as page slices are migrated.
 *
 * Deps: UserStore (core), store (core), Money (core), I18nFormatter (core), React
 */
import React from 'react';
import UserStore from '../stores/UserStore';
import store from '../stores/redux';
import Money from '../utils/Money/Money';
import { I18nFormatter } from '../utils/I18n/I18n';
import { mimeTypes } from '../config';

export const getPaymentMethod = (paymentMethod) => {
    return store.getState().paymentMethods.find((payment) => payment.name === paymentMethod) || {};
};

export const getPaymentSystem = (paymentSystem) => {
    return store.getState().paymentSystems.find((system) => system.name === paymentSystem) || {};
};

export const getCurrencyItemInterval = (createdAt, expiredAt, pr) => {
    const params = {
        from: '',
        to: ' — ',
        ...pr
    };

    let result = `${params.from}${I18nFormatter.fullDateTime(+createdAt)}`;

    if (expiredAt) {
        result += `${params.to}${I18nFormatter.fullDateTime(expiredAt)}`;
    }

    return result;
};

export const getDataCurrency = (data) => {
    const currencyNames = UserStore.getTranslation().currencyPage.table;
    const currentCurrency = store.getState().user?.publisher?.account?.balance?.currency;
    const rates = data.rates[currentCurrency];
    let item = {
        interval: getCurrencyItemInterval(data.created_at, data.expired_at, {
            from: `${currencyNames.from} `,
            to: ` ${currencyNames.to} `
        })
    };

    Object.keys(rates).map((key) => {
        let rate = rates[key];

        if (rate.ask === 1) return false;

        item[`${currentCurrency}/${key}`] = Money.toFixedRatio(rate.ask, key, true);
    });

    return item;
};

export const getConversionStatuses = (statuses) => {
    return statuses.map((status) => getConversionStatus(status, status));
};

export const getConversionStatus = (status, key) => {
    const staticNames = UserStore.getTranslation().conversionStatuses;

    return <span className="label" key={key}>{staticNames[status]}</span>;
};

export const getStatus = (status) => {
    const staticNames = UserStore.getTranslation().statuses;
    const classStatus = {
        'new': 'label-primary',
        'canceled': 'label-danger',
        'paid': 'label-warning',
        'sent': 'label-success',
        'failed': 'label-danger'
    };

    return <span className={`label ${classStatus[status]}`}>{staticNames[status]}</span>;
};

export const getRegionNameRecursively = (region, params = {}, iteration = 0) => {
    const type = params.type || 'lastFirst';
    const delimiter = params.delimiter || '-';
    let iter = iteration;
    let name = '';
    let parent = '';

    if (region.parent) {
        parent = getRegionNameRecursively(region.parent, params, iter += 1);
    }

    if (type === 'all' || (type === 'lastFirst' && !region.parent) || (type === 'lastFirst' && iteration === 0)) {
        if ((iteration === 0 && !region.parent) || !region.parent) {
            name = region.title;
        } else {
            name = `${parent}${delimiter}${region.title}`;
        }
    } else if (type === region.type) {
        name = region.title;
    } else {
        name = `${parent}${name}`;
    }

    return name;
};

export const getMimeType = (mimeType) => {
    return mimeTypes[mimeType] || 'Static';
};

export const getHostingType = (isExternal) => {
    if (isExternal) {
        return 'hostingTypeExternal';
    }

    return 'hostingTypeInternal';
};
